import type { CSSProperties, JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NAMESPACES } from "@/i18n/constants";
import { cn } from "@/utils/class-name";
import {
    CONTROL_BORDER,
    INK,
    LIVE_DOT,
    STALE_DOT,
    STALE_PILL_BG,
    TEXT_ON_INK,
    TEXT_ON_INK_MUTED,
    TEXT_SECONDARY,
    TEXT_STALE,
} from "../panel-tokens";
import type { ReadoutMode } from "./readout";
import "./time-pill.css";

export const PILL =
    "inline-flex h-[18px] flex-none items-center gap-[4px] rounded-[10px] px-[6px] text-[11px] leading-[14px] whitespace-nowrap";

const INK_PILL: CSSProperties = { backgroundColor: INK };
const STALE_PILL: CSSProperties = { backgroundColor: STALE_PILL_BG, border: `1px solid ${CONTROL_BORDER}` };

function Pill({ style, children }: { style: CSSProperties; children: ReactNode }) {
    return (
        <span className={PILL} style={style}>
            {children}
        </span>
    );
}

function Dot({ color, pulses = false }: { color: string; pulses?: boolean }) {
    return (
        <span
            aria-hidden
            className={cn("size-[6px] rounded-full", pulses && "time-pill-live-dot")}
            style={{ backgroundColor: color }}
        />
    );
}

/** Label, separator and time, a tighter 3px apart than the pill's 4px gap after the dot. */
const TEXT = "flex gap-[3px]";

function Label({
    color,
    separatorColor,
    medium = false,
    children,
}: {
    color: string;
    separatorColor: string;
    medium?: boolean;
    children: ReactNode;
}) {
    return (
        <>
            <span className={cn(medium && "font-medium")} style={{ color }}>
                {children}
            </span>
            <span aria-hidden style={{ color: separatorColor }}>
                ·
            </span>
        </>
    );
}

function Time({ color, children }: { color: string; children: ReactNode }) {
    return (
        <span className={"font-medium tabular-nums"} style={{ color }}>
            {children}
        </span>
    );
}

/** A past range has no pill: it reads nothing until hovered. */
type TimePillMode = Exclude<ReadoutMode, "past">;

export function TimePill({ mode, time }: { mode: TimePillMode; time: string }): JSX.Element {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "readout" });

    switch (mode) {
        case "live":
            return (
                <Pill style={INK_PILL}>
                    <Dot color={LIVE_DOT} pulses />
                    <span className={TEXT}>
                        <Label color={TEXT_ON_INK} separatorColor={TEXT_ON_INK_MUTED} medium>
                            {t("latest")}
                        </Label>
                        <Time color={TEXT_ON_INK}>{time}</Time>
                    </span>
                </Pill>
            );
        case "stale":
            return (
                <Pill style={STALE_PILL}>
                    <Dot color={STALE_DOT} />
                    <span className={TEXT}>
                        <Label color={TEXT_SECONDARY} separatorColor={TEXT_SECONDARY}>
                            {t("last-seen")}
                        </Label>
                        <Time color={TEXT_STALE}>{time}</Time>
                    </span>
                </Pill>
            );
        case "cursor":
            return (
                <Pill style={INK_PILL}>
                    <Time color={TEXT_ON_INK}>{time}</Time>
                </Pill>
            );
        default: {
            // Lint wants a default; the never keeps a new mode a compile error instead of a silent gap.
            const unhandled: never = mode;
            throw new Error(`No time pill for mode ${String(unhandled)}`);
        }
    }
}
